import { createSelector } from '@reduxjs/toolkit';
import { Device } from '../api/device.model';
import DeviceModel from '../api/DeviceModel';
import { RootState } from '../../store.model';
import { RouterComponentProps } from '../../../util/route-dom';
import { uniq } from 'lodash';
import GroupModel from '../../groups/api/GroupModel';
import { GroupAPI } from '../../groups/api/GroupAPI';
import { isNotNullOrUndefined } from '../../../util/types/Array';
import { CommanderAPI } from '../api/CommanderAPI';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  getDictionaryGroups,
  getGroupByGroupIdSelector,
  getGroupIdQueryParam,
  getGroupIds,
  getGroupSelectedByQueryParamSelector,
} from '../../groups/selectors';
import { getControlUnitQueryParam } from '../../controlUnit/selectors';
import { getLevelOfView } from '../../groups/selectors/getLevelOfView';

export type RouteComponentPropsDevice = Pick<
  RouterComponentProps<{
    deviceId?: string;
    groupId?: string;
  }>,
  'params'
>;

const getDevicesResponse = (state: RootState) =>
  CommanderAPI.endpoints.getCommandersByIds.select(
    getAllDevicesIds(state) ?? skipToken
  )(state);

export const getAllDevicesIdsOnGroupsSelector = createSelector(
  [getDictionaryGroups, getGroupIds],
  GroupModel.getAllDevicesIdFromGroups
);
const getGroupIdProps = (
  _: RootState,
  props: {
    groupId?: string;
  }
) => props.groupId;

const getDevicesIdsProps = (
  _: RootState,
  props: {
    devicesIds?: string[];
  }
) => props.devicesIds;

export const getDevicesIdsFromGroupSelected = createSelector(
  [
    getGroupIdProps,
    GroupAPI.endpoints.getGroups.select(undefined),
    CommanderAPI.endpoints.getAssociatedDevices.select(null),
  ],
  (groupId, groupsDictionary, devicesAssociated) =>
    groupId
      ? groupsDictionary.data?.entities.groups[groupId].devices
      : devicesAssociated.data?.result
);

export const getDeviceDictionary = (state: RootState, props: {}) => {
  return getDevicesResponse(state).data?.entities?.devices;
};

export const getDevicesFromGroupSelected = createSelector(
  [getDeviceDictionary],
  (devicesDictionary) =>
    devicesDictionary ? Object.values(devicesDictionary) : undefined
);

// ^ Functionality inherit
export const getDevicesData = createSelector(
  [getDeviceDictionary],
  (devicesDictionary) => {
    return devicesDictionary;
  }
);

export const getDevicesDataLoadingById = (state: RootState) => {
  return getDevicesResponse(state).isLoading;
};

export const getDevicesLabelById = (
  state: RootState,

  props: { deviceId: string }
) => state.devices.devicesLabels[props.deviceId];

export const getAssociatedDevices = createSelector(
  (state: RootState) =>
    CommanderAPI.endpoints.getAssociatedDevices.select(null)(state),
  (api) => api.data?.result
);

export const getDeviceIdFromQueryParam = (
  _: RootState,
  props: RouteComponentPropsDevice
) => props.params.deviceId;

export const getDeviceIdFromProps = (
  _: RootState,
  props: { deviceId: string }
) => props.deviceId;

export const queryPramsToProps = (
  _: RootState,
  props: RouteComponentPropsDevice
) => props.params;

export const getDeviceDataByIdParams = createSelector(
  [getDevicesData, getDeviceIdFromProps],
  (devicesDictionary, deviceId) => devicesDictionary?.[deviceId]
);
export const getDeviceDataById = createSelector(
  (_state: RootState, props: { deviceId: string }) => props.deviceId,
  getDevicesData,
  (deviceId, devicesDictionary) => devicesDictionary?.[deviceId]
);

export const getDevicesDataById = createSelector(
  [getDevicesData, getDevicesIdsProps],
  (devicesDictionary, devicesIds) => {
    return devicesIds?.map((deviceId) => devicesDictionary?.[deviceId]);
  }
);

const model = new DeviceModel();

export const getDeviceWithLoadingFromProps = createSelector(
  [getDeviceIdFromProps, getDeviceDataByIdParams, getDevicesDataLoadingById],
  (deviceId, deviceData, loadingDevicesData): Device => {
    return {
      uuid: deviceId,
      ...deviceData,
      loadingData: loadingDevicesData,
    };
  }
);

const emptyDevices: Device[] = [];
export const getDevicesByIds = createSelector(
  [getDevicesIdsProps, getDevicesDataLoadingById, getDeviceDictionary],
  (devicesIds, loadingDevicesData, devicesDictionary): Device[] => {
    return (
      devicesIds?.map((deviceId) => ({
        ...devicesDictionary?.[deviceId],
        loadingData: loadingDevicesData,
        uuid: deviceId,
      })) ?? emptyDevices
    );
  }
);

export const getAllDevicesIds = createSelector(
  [getAllDevicesIdsOnGroupsSelector, getAssociatedDevices],
  (devicesIdsOnGroups, associatedDevices = []) =>
    uniq([...devicesIdsOnGroups, ...associatedDevices]).sort()
);

export const getDevices = createSelector(
  [getAllDevicesIds, getDevicesDataLoadingById, getDeviceDictionary],
  (devicesIds, loadingDevicesData, devicesDictionary): Device[] => {
    return (
      devicesIds?.map((deviceId) => ({
        ...devicesDictionary?.[deviceId],
        loadingData: loadingDevicesData,
        uuid: deviceId,
      })) ?? emptyDevices
    );
  }
);

export const getNumDevices = (state: RootState) =>
  Object.keys(state.devices.dictionaryDevicesData).length;

export const getDeviceSelected = createSelector(
  [(state) => state, getDeviceIdFromQueryParam],
  (state, deviceId = '') => getDeviceWithLoadingFromProps(state, { deviceId })
);
export const getDeviceSelectedName = createSelector(
  [getDeviceSelected],
  (device) => device?.name || ''
);

export const isDeviceInAssociatedDevices = createSelector(
  [getDeviceIdFromProps, getAssociatedDevices],
  (deviceId, devicesIds) => devicesIds?.includes(deviceId)
);

export const getAllDevicesIdsByMode = createSelector(
  [
    getGroupSelectedByQueryParamSelector,
    getDictionaryGroups,
    getGroupIdQueryParam,
    getDeviceIdFromQueryParam,
    getControlUnitQueryParam,
    getAllDevicesIds,
  ],
  (
    groupSelected,
    dictionaryGroups,
    groupId,
    deviceId,
    controlUnitId,
    allDevicesIds
  ) => {
    const level = getLevelOfView({ controlUnitId, deviceId, groupId });
    switch (level) {
      case 'CONTROL_UNIT':
        return [deviceId as string];
      case 'DEVICE':
        return [deviceId as string];
      case 'GROUP':
        return GroupModel.getAllDevicesIdFromGroups(
          dictionaryGroups,
          undefined,
          groupSelected?.id
        );
      case 'ALL_GROUPS':
        return allDevicesIds;
      default:
        return [];
    }
  }
);

export const getAllavailableDevicesIdsToAddByGroupIdSelector = createSelector(
  [getGroupByGroupIdSelector, getAllDevicesIds],
  (group, allDevicesIds) =>
    allDevicesIds.filter((id) => !group?.devices?.includes(id))
);
const emptyString: string[] = [];
export const getDevicesIdsFromGroupSelectedByQueryParamSelector = (
  state: RootState,
  props: RouteComponentPropsDevice
): string[] => {
  const group = getGroupSelectedByQueryParamSelector(state, props);
  if (group) {
    return group.devices ?? emptyString;
  }
  // loading groups...
  if (props.params.groupId) return emptyString;
  return getAssociatedDevices(state) ?? emptyString;
};

export const getDevicesFromGroupSelectedByQueryParamSelector = (
  state: RootState,
  props: RouteComponentPropsDevice
): Device[] => {
  return getDevicesByIds(state, {
    devicesIds: getDevicesIdsFromGroupSelectedByQueryParamSelector(
      state,
      props
    ),
  });
};

/**
 * getAllavailableDevicesToAddByGroupIdSelector
 * @return allDevices - devicesGroup
 * Child are memoized, so it no need be memoized, because too much cache
 *
 * This function replace functionality of Set Options:
 * Set Options:
 * This function uses the arrays:
 * - devicesGroups (all the existing devices in the groups )
 * - devicesGroup (all the existing devices that belong to the group)
 * - devices (all the devices that are not associated to any group but with the user)
 * Returns an array with the devices options ( = devicesGroups + devices - devicesGroup)
 * This options are available devices to be associated to the group
 */
export const getAllAvailableDevicesToAddByGroupIdSelector = (
  state: RootState,
  props: { groupId?: string }
) => {
  const devicesIds = getAllavailableDevicesIdsToAddByGroupIdSelector(
    state,
    props
  );
  return getDevicesByIds(state, { devicesIds });
};
